import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import { dashboardAPI, gamificationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import './DashboardPage.css';

const number = value => Number(value || 0).toLocaleString('pt-BR');
const localDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const initialRange = () => {
  const now = new Date();
  return { start: localDate(new Date(now.getFullYear(), now.getMonth(), 1)), end: localDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
};
const displayDate = date => date ? date.slice(0, 10).split('-').reverse().join('/') : '—';

export default function DashboardPage() {
  const { user } = useAuth();
  const admin = user?.role === 'ADMIN';
  const [range, setRange] = useState(initialRange);
  const [nameInput, setNameInput] = useState('');
  const [name, setName] = useState('');
  const [page, setPage] = useState(1);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ranking, setRanking] = useState(null);
  const [rankingError, setRankingError] = useState('');
  const [view, setView] = useState('points');
  const [paused, setPaused] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const scrollRef = useRef(null);
  const invalid = !range.start || !range.end || range.start > range.end;

  useEffect(() => {
    const timer = setTimeout(() => { setName(nameInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [nameInput]);
  useEffect(() => {
    if (invalid) return;
    let active = true;
    setLoading(true);
    setError('');
    dashboardAPI.getPoints({ ...range, name: admin ? name : undefined, page, page_size: 30 })
      .then(({ data }) => { if (active) setLedger(data); })
      .catch(() => { if (active) { setLedger(null); setError('Não foi possível carregar as pontuações.'); } })
      .finally(() => { if (active) setLoading(false); });
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    return () => { active = false; };
  }, [range, name, page, admin, invalid, refresh]);
  useEffect(() => {
    if (!admin) return;
    let active = true;
    const load = () => gamificationAPI.getMonthlyRanking()
      .then(({ data }) => { if (active) { setRanking(data); setRankingError(''); } })
      .catch(() => { if (active) setRankingError('Não foi possível atualizar o ranking mensal.'); });
    load();
    const timer = setInterval(load, 60000);
    return () => { active = false; clearInterval(timer); };
  }, [admin, refresh]);
  useEffect(() => {
    if (!admin || paused) return;
    const timer = setInterval(() => {
      if (!document.hidden) setView(current => current === 'points' ? 'ranking' : 'points');
    }, 5000);
    return () => clearInterval(timer);
  }, [admin, paused]);

  const pause = () => { if (admin) setPaused(true); };
  const changeDate = (key, value) => { pause(); setRange(current => ({ ...current, [key]: value })); setPage(1); };
  const totalPages = Math.max(1, Math.ceil((ledger?.total || 0) / 30));
  const pending = loading || invalid;
  return <Layout><div className="points-dashboard space-y-6" data-testid="dashboard">
    <div><h1 className="text-3xl font-bold text-white">Dashboard</h1><p className="text-slate-400 mt-2">{admin ? 'Acompanhe os pontos dos usuários e o ranking do mês.' : `Olá, ${user?.full_name?.split(' ')[0] || ''}! Acompanhe sua pontuação.`}</p></div>
    {admin && <div className="flex flex-wrap gap-2 items-center" aria-label="Telas do dashboard">
      <Button aria-pressed={view === 'points'} variant={view === 'points' ? 'default' : 'outline'} onClick={() => { setView('points'); pause(); }}>Pontuações</Button>
      <Button aria-pressed={view === 'ranking'} variant={view === 'ranking' ? 'default' : 'outline'} onClick={() => { setView('ranking'); pause(); }}>Ranking do mês</Button>
      <Button variant="outline" onClick={() => setPaused(current => !current)}>{paused ? 'Retomar alternância' : 'Pausar alternância'}</Button>
      <span className="text-xs text-slate-400">{paused ? 'Alternância pausada para consulta' : 'Alternância automática a cada 5 segundos'}</span>
    </div>}
    <section hidden={admin && view !== 'points'} aria-label="Pontuações" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" onFocusCapture={pause}>
        <label className="text-sm text-slate-300">Data inicial<Input type="date" value={range.start} onChange={e => changeDate('start', e.target.value)} className="mt-2 w-full min-w-0" /></label>
        <label className="text-sm text-slate-300">Data final<Input type="date" value={range.end} onChange={e => changeDate('end', e.target.value)} className="mt-2 w-full min-w-0" /></label>
        {admin && <label className="text-sm text-slate-300">Nome do usuário<Input value={nameInput} maxLength={200} placeholder="Todos os usuários" onChange={e => { pause(); setNameInput(e.target.value); }} className="mt-2" /></label>}
      </div>
      {invalid && <p role="alert" className="text-amber-300">Informe um período válido, com a data inicial até a final.</p>}
      <div className="rounded-xl border border-white/10 bg-white/5 p-5">
        <p className="text-slate-400">{admin ? 'Pontuação total dos usuários filtrados' : 'Sua pontuação total'} no período</p>
        <p className="text-4xl font-bold text-white mt-2" data-testid="points-total">{pending || error ? '—' : number(ledger?.total_points)} <span className="text-lg font-normal">pontos</span></p>
        <p className="text-xs text-slate-400 mt-3">Soma de relatos avaliados e eventos ativos. Inclui as duas datas; relatos pela data do convite e eventos pela data do lançamento.</p>
      </div>
      <h2 className="text-xl font-semibold text-white">Histórico de pontuações</h2>
      {error ? <div role="alert">{error} <Button onClick={() => setRefresh(n => n + 1)}>Tentar novamente</Button></div> : pending ? <p role="status" className="text-slate-400">{invalid ? 'Ajuste o período para consultar.' : 'Carregando pontuações…'}</p> : <>
        <div ref={scrollRef} className="points-table-scroll" tabIndex={0} role="region" aria-label="Histórico de pontuações com rolagem" onFocusCapture={pause} onWheel={pause} onTouchStart={pause}>
          <table className="points-table"><thead><tr><th>Data</th>{admin && <th>Usuário</th>}<th>Origem</th><th>Descrição</th><th className="points-number">Pontos</th></tr></thead>
            <tbody>{ledger?.items.map(row => <tr key={row.id}><td className="whitespace-nowrap">{displayDate(row.date)}</td>{admin && <td>{row.full_name}</td>}<td>{row.source}</td><td className="points-description">{row.description || 'Sem descrição'}</td><td className="points-number">{number(row.points)}</td></tr>)}</tbody>
          </table>
          {!ledger?.items.length && <p className="p-6 text-center text-slate-400">Nenhuma pontuação encontrada neste período.</p>}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400"><span>{number(ledger?.total)} registros · Página {page} de {totalPages}</span><div className="flex gap-2"><Button variant="outline" disabled={page <= 1} onClick={() => { pause(); setPage(n => n - 1); }}>Anterior</Button><Button variant="outline" disabled={page >= totalPages} onClick={() => { pause(); setPage(n => n + 1); }}>Próxima</Button></div></div>
      </>}
    </section>
    {admin && <section hidden={view !== 'ranking'} className="space-y-4" aria-label="Ranking do mês">
      <h2 className="text-2xl font-semibold text-white">Ranking de gamificação · {new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'America/Sao_Paulo' })}</h2>
      <p className="text-sm text-slate-400">Mês corrente, com todos os participantes elegíveis. Atualizado a cada minuto.</p>
      {rankingError && <p role="alert">{rankingError} <Button onClick={() => setRefresh(n => n + 1)}>Tentar novamente</Button></p>}
      {!ranking ? !rankingError && <p role="status">Carregando ranking…</p> : <div className="points-table-scroll" tabIndex={0} role="region" aria-label="Ranking mensal com rolagem" onFocusCapture={pause} onWheel={pause} onTouchStart={pause}>
        <table className="points-table"><thead><tr><th>Posição</th><th>Usuário</th><th className="points-number">Pontos</th></tr></thead><tbody>{ranking.map((row, index) => <tr key={row.user_id}><td>{index + 1}º</td><td>{row.full_name}</td><td className="points-number">{number(row.total_points)}</td></tr>)}</tbody></table>
        {!ranking.length && <p className="p-6 text-center text-slate-400">Nenhum ponto registrado neste mês.</p>}
      </div>}
    </section>}
  </div></Layout>;
}
